
import {Box, Button, Dialog, DialogContent, Divider, Grid, Skeleton, Stack, Typography} from "@mui/material";
import {Icon} from "@iconify/react";
import React, {useContext, useEffect, useState} from "react";
import CheckIcon from "@mui/icons-material/Check";

import BaseDialogTitle from "../../../../components/dialog/BaseDialogTitle";
import {
    ActivityFields,
    ActivityOnboardingComplete, ActivityOnboardingCompleteFields,
    ActivitySolicitation, ActivityStatusStory
} from "../../../../types/activity/activityData";
import {dateFormatter} from "../../../../utils/formatters";
import {useForm} from "react-hook-form";
import {
    HttpActivityFile,
    HttpActivityStatusStory,
    HttpOnboardingActivity
} from "../../../../http/onboarding/httpOnboardingActivity";
import {BaseResponseFields, EntityWithIdFields} from "../../../../types/baseEntities";
import {useAction} from "../../../../hooks/useAction";
import {ControlledTextField} from "../../../../components/forms";
import {LoaderBlockUI} from "../../../../components/loader";
import SolicitationTaskTimelineDetail from "./SolicitationTaskTimelineDetail";
import {ActivityStatus} from "../../../../types/activity/activityEnums";
import {getActivityStatusColor} from "../../../../utils/colors/activityColors";
import CardDocumentationContent from "../CardDocumentationContent";
import {CompanyContext, CompanyContextValue} from "../../../solicitation-new/context/companyContext";
import {FileBase, FileBaseFields} from "../../../../types/solicitation/solicitationFile";
import {ActivityFile} from "../../../../types/activity/activityFile";
import {fileFormatter} from "../../../../utils/formatters/fileFormatter";
import DialogAlert from "../../../../components/dialog/DialogAlert";
import {HttpFiles} from "../../../../http/files/httpFiles";
import {DeleteIconButton, DownloadIconButton, SearchIconButton} from "../../../../components/buttons/Buttons";
import DialogPreviewFile from "../../../../components/dialog/DialogPreviewFile";

interface SolicitationTaskDetailDialogProps {
    task: ActivitySolicitation,
    open: boolean,
    onClose: () => void,
    onReload: () => void,
    solicitationId: number
}


const SolicitationTaskDetailDialog = ({task, open, onClose, onReload, solicitationId}: SolicitationTaskDetailDialogProps) => {
    const [openAlert, setOpenAlert] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)
    const [loadingFiles, setLoadingFiles] = useState<boolean>(false)
    const {snackbarError, snackbarSuccess} = useAction()
    const methods = useForm<ActivityOnboardingComplete>({
        defaultValues: {
            [ActivityOnboardingCompleteFields.Observation]: task[ActivityFields.Observations]
        }
    })
    const taskPendant: boolean = task[ActivityFields.ActivityStateCode] == ActivityStatus.ConSeguimiento_Pendiente || task[ActivityFields.ActivityStateCode] == ActivityStatus.ConSeguimiento_EnCurso
    
    const [lstStatusStory, setStatusStory] = useState<ActivityStatusStory[]>([])
    const [files, setFiles] = useState<ActivityFile[]>([])
    const [deleteFile, setDeleteFile] = useState<FileBase>()
    const [fileName, setFileName] = useState<string>()
    const [fileBlob, setFileBlob] = useState<any>()
    const [openPreview, setOpenPreview] = useState<boolean>(false)
    const { clientId } = useContext<CompanyContextValue>(CompanyContext)
    const [openDelete, setOpenDelete] = useState<boolean>(false)

    const colorStatus : string = getActivityStatusColor(task[ActivityFields.ActivityStateCode])
    
    const onCloseAlert = () => {
        setOpenAlert(false)
    }
    
    const onOpenAlert = () => setOpenAlert(true)
    const watchObservations = methods.watch(ActivityOnboardingCompleteFields.Observation)
    
    const onSubmitTask = (data: ActivityOnboardingComplete) => {
        const submitData: ActivityOnboardingComplete = {
            [ActivityOnboardingCompleteFields.Observation]: data[ActivityOnboardingCompleteFields.Observation] || '',
            [ActivityOnboardingCompleteFields.SolicitationId]: solicitationId
        }
        
        setLoading(true)
        
        HttpOnboardingActivity.complete(task[EntityWithIdFields.Id], submitData).then((r) => {
            if (r[BaseResponseFields.HasError]) {
                snackbarError(r[BaseResponseFields.ErrorDescription])
            }else {
                snackbarSuccess('La tarea se resolvió con éxito')
                onCloseAlert()
                onReload()
                onClose()
            }
            setLoading(false)
        })
    }
    
    const loadFiles = () => {
        setLoadingFiles(true)
        HttpActivityFile.getList(task[EntityWithIdFields.Id]).then((r) => {
            setFiles(r)
        }).finally(() => setLoadingFiles(false))
    }

    useEffect(() => {
        if (open) {
            loadFiles()
        }
    }, [open]);
    
    useEffect(() => {
        if (!taskPendant) {
            methods.setValue(ActivityOnboardingCompleteFields.Observation, task[ActivityFields.Observations])
        }
        setLoading(true)
        
        /*
            HttpActivity.getById(task[EntityWithIdFields.Id]).then((r) => {
                setObservations(r[ActivityFields.Observations])
    
                HttpActivityStatusStory.getByIdActivity(task[EntityWithIdFields.Id])
                    .then((res) => {
                        setStatusStory(res);
                        setLoading(false);
                    })
            })
         */

        HttpActivityStatusStory.getByIdActivity(task[EntityWithIdFields.Id])
            .then((res) => {
                setStatusStory(res);
                setLoading(false);
            })
        
        loadFiles()
    }, [])

    const saveNewDoc = (fileBase?: FileBase, file?: File, fileId?: number) => {
        return HttpActivityFile.insertFile(task[EntityWithIdFields.Id], fileBase, file, fileId)
    }

    const onDownloadFile = (file: FileBase) => {
        HttpFiles.download(file[EntityWithIdFields.Id])
            .then((data) => {
                const downloadUrl = window.URL.createObjectURL(new Blob([data]));

                const link = document.createElement('a');
                link.href = downloadUrl;

                link.setAttribute('download', file.descArchivo);

                document.body.appendChild(link);
                link.click();
                link.remove();
            });
    }

    const onPreviewFile = (file: ActivityFile) => {
        setFileName(file[FileBaseFields.FileDesc])
        setLoading(true)

        HttpFiles.download(file[EntityWithIdFields.Id]).then((data) => {
            setFileBlob(data)
            setLoading(false)
            setOpenPreview(true)
        })
    }

    const onClosePreview = () => {
        setFileBlob(undefined)
        setOpenPreview(false)
    }
    
    const onDeleteFile = (file: ActivityFile) => {
        setDeleteFile(file)
        setOpenDelete(true)
    }
    
    const onCloseDelete = () => {
        setDeleteFile(undefined)
        setOpenDelete(false)
    }
    
    const onConfirmDelete = () => {
        if (deleteFile) {
            HttpFiles.delete(deleteFile[EntityWithIdFields.Id]).then(() => loadFiles())
        }
    }
    
    return (
        <Dialog open={open}
                onClose={onClose}
                maxWidth={'xl'}
                fullWidth
        >
            <BaseDialogTitle title={task[ActivityFields.Reference]} onClose={onClose}/>
            <DialogContent>
                <Divider sx={{mb: 2}}/>
                    <Grid container spacing={6} mt={2}>
                        <Grid item md={5.75}>
                            <form onSubmit={methods.handleSubmit(onSubmitTask)} id={'task-form'}>
                                <Stack spacing={3}>
                                        <Stack direction='row' display='flex' justifyContent='space-between' alignItems='center'>
                                            <Typography fontSize={16} fontWeight={600}>
                                                Fecha de inicio:
                                            </Typography>
                                            <Typography fontSize={16}>
                                                {dateFormatter.toShortDate(task[ActivityFields.ActivityDate])}
                                            </Typography>
                                        </Stack>
                                        <Stack direction='row' display='flex' justifyContent='space-between' alignItems='center' pt={4}>
                                            <Typography fontWeight={600} fontSize={16}>Estado:</Typography>
                                            <Box
                                                sx={{ display: 'flex', alignItems: 'center', '& svg': { mr: 1.8, color: `${colorStatus}` } }}
                                            >
                                                <Icon icon='mdi:circle' fontSize='1.3rem' />
                                                <Typography fontSize={16}>{task[ActivityFields.ActivityStateDesc]}</Typography>
                                            </Box>
                                        </Stack>
                                        <Stack direction='row' display='flex' justifyContent='space-between' alignItems='center'>
                                            <Typography fontWeight={600} fontSize={16}>Informador:</Typography>
                                            <Typography fontSize={16}>{task[ActivityFields.ResponsibleUserDesc]}</Typography>
                                        </Stack>
                                        <Stack direction='row' display='flex' justifyContent='space-between' alignItems='center'>
                                            <Typography fontWeight={600} fontSize={16}>Motivo:</Typography>
                                            <Typography fontSize={16}>{task[ActivityFields.MotiveDesc]}</Typography>
                                        </Stack>
                                    
                                        {
                                            
                                            /*
                                            <Stack direction='row' display='flex' justifyContent='space-between' spacing={3}>
                                                <Typography fontWeight={600} fontSize={16}>Observaciones:</Typography>
                                                {
                                                    loading ? <Skeleton /> : <Typography fontSize={16}>{observations}</Typography>
                                                }
                                            </Stack>
                                             */
                                        }
                                    {
                                        taskPendant &&
                                        <ControlledTextField id="outlined-multiline-flexible"
                                                             multiline
                                                             control={methods.control}
                                                             name={ActivityOnboardingCompleteFields.Observation}
                                                             rows={3} 
                                                             label="Comentarios" 
                                                             fullWidth
                                                             disabled={!taskPendant}
                                                             sx={{paddingTop: 2}}
                                        />
                                        
                                            /*
                                            <Stack direction='row' display='flex' justifyContent='space-between' alignItems='center'>
                                                <Typography fontWeight={600} fontSize={16}>Comentarios realizados:</Typography>
                                                {loading ? <Skeleton /> :
                                                    <Typography fontSize={16}>{lstStatusStory[0] ? lstStatusStory[0].vchObservacionEstado : ''}</Typography>
                                                }
                                            </Stack>
                                            
                                             */
                                    }
                                    {
                                        openAlert &&
                                        <DialogAlert open={openAlert}
                                                     onClose={onCloseAlert}
                                                     submitId={'task-form'}
                                                     title={'Resolver tarea'}
                                                     subtitle={'¿Está seguro que desea resolver esta tarea?'}
                                        />
                                    }
                                    </Stack>
                            </form>
                            <Stack spacing={2} mt={9}>
                                        {
                                            loadingFiles ?
                                                <Stack spacing={1}>
                                                    <Skeleton width={'100%'}/>
                                                    <Skeleton width={'100%'}/>
                                                </Stack>
                                                :
                                                files.length !== 0 ?
                                                    <Stack spacing={1}>
                                                        <Typography fontSize={16} fontWeight={500} mb={2}>Archivos subidos</Typography>
                                                        <Divider />
                                                        {files.map((file, idx) =>
                                                            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} key={idx}>
                                                                <Stack direction={'row'} alignItems={'center'} spacing={2}>
                                                                    {fileFormatter.getIconByFileName(file[FileBaseFields.FileDesc], {fontSize: 'small'})}
                                                                    <Stack>
                                                                        <Typography fontSize={14} fontWeight={500}>{file[FileBaseFields.FileDesc]}</Typography>
                                                                        <Typography fontSize={10} fontWeight={500} color={'text.disabled'}>{`Tipo: ${file[FileBaseFields.FileTypeDesc]}`}</Typography>
                                                                    </Stack>
                                                                </Stack>
                                                                <Stack direction={'row'} alignItems={'center'} spacing={2}>
                                                                    <SearchIconButton onClick={() => {onPreviewFile(file)}} size={'small'} tooltipTitle={'Previsualizar'}/>
                                                                    <DownloadIconButton size={'small'} onClick={() => {onDownloadFile(file)}} tooltipTitle={'Descargar'}/>
                                                                    {taskPendant && <DeleteIconButton size={'small'} onClick={() => {onDeleteFile(file)}} tooltipTitle={'Eliminar'} />}
                                                                </Stack>
                                                            </Stack>
                                                        )}
                                                    </Stack>
                                                    :
                                                    <></>
                                        }
                                        {
                                            taskPendant ?
                                             <CardDocumentationContent clientId={clientId}
                                                                       fileType={36}
                                                                       onNewDoc={saveNewDoc}
                                                                       size={'small'}
                                                                       onReload={loadFiles}
                                             />
                                             :
                                             <>
                                             </>
                                        }
                                        {
                                            taskPendant &&
                                            <Box textAlign={'center'} sx={{paddingTop: 2}}>
                                                <Button variant={"outlined"}
                                                        color={"primary"}
                                                        endIcon={<CheckIcon />}
                                                        onClick={onOpenAlert}
                                                        disabled={!watchObservations && (!files || files.length == 0)}
                                                >
                                                    Resolver tarea
                                                </Button>
                                            </Box>
                                        }
                            </Stack>
                        </Grid>
                        <Grid item md={0.5}>
                            <Divider orientation={'vertical'} sx={{height: '100% !important'}}/>
                        </Grid>
                        <Grid item md={5.75}>
                            <SolicitationTaskTimelineDetail lstStatus={lstStatusStory} loading={loading} taskId={task[EntityWithIdFields.Id]}/>
                        </Grid>
                    </Grid>
            </DialogContent>
            {
                deleteFile && openDelete &&
                <DialogAlert open={openDelete}
                             onClose={onCloseDelete}
                             title={'Eliminar archivo'}
                             subtitle={`¿Está seguro que desea eliminar el archivo ${deleteFile[FileBaseFields.FileDesc]}?`}
                             onAccept={onConfirmDelete}
                />
            }
            {
                fileBlob && fileName &&
                <DialogPreviewFile open={openPreview}
                                   onClose={onClosePreview}
                                   fileBlob={fileBlob}
                                   fileName={fileName}
                />
            }
            {loading && <LoaderBlockUI />}
        </Dialog>
    )
}


export default SolicitationTaskDetailDialog